/**
 * PhotoCard Component
 * Individual photo card displaying thumbnail, metadata, and actions
 * Shows photo info and delete button for owners/admins
 *
 * @module components/gallery/PhotoCard
 */

import React, { useEffect } from 'react';
import { Photo, User } from '@/types';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoCardProps {
  photo: Photo;
  currentUser?: User | null;
  onPhotoClick: (photo: Photo) => void;
  onDelete?: (photoId: string) => void;
  isDeleting?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Photo card component displaying a single photo thumbnail with metadata
 * Shows delete button for photo owner or admins
 * Handles loading states and error fallback
 *
 * @component
 * @example
 * <PhotoCard
 *   photo={photo}
 *   currentUser={user}
 *   onPhotoClick={handlePhotoClick}
 *   onDelete={handleDelete}
 * />
 */
const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  currentUser,
  onPhotoClick,
  onDelete,
  isDeleting = false,
}) => {
  // Debug logging
  useEffect(() => {
    logger.debug(`[WALRUS] PhotoCard: mounted`, {
      photoId: photo.id,
      filename: photo.filename,
      canDelete: currentUser?.id === photo.uploadedBy || currentUser?.role === 'admin',
    });
  }, [photo.id, photo.filename, photo.uploadedBy, currentUser]);

  // Check if current user can delete this photo
  const canDelete =
    currentUser && (currentUser.id === photo.uploadedBy || currentUser.role === 'admin');

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  /**
   * Handle card click - open lightbox
   */
  const handleCardClick = () => {
    logger.debug(`[WALRUS] PhotoCard: clicked`, { photoId: photo.id });
    onPhotoClick(photo);
  };

  /**
   * Handle delete button click
   */
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    logger.debug(`[WALRUS] PhotoCard: delete clicked`, { photoId: photo.id });

    if (onDelete && !isDeleting) {
      onDelete(photo.id);
    }
  };

  return (
    <div
      className="group relative overflow-hidden rounded-lg bg-walrus-white border border-walrus-gray transition-all duration-200 hover:shadow-lg hover:border-walrus-black cursor-pointer"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-walrus-cream">
        {photo.thumbnailUrl || photo.url ? (
          <>
            <img
              src={photo.thumbnailUrl || photo.url}
              alt={photo.filename}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
            {/* Overlay on hover */}
            <div className="absolute inset-0 bg-black bg-opacity-0 transition-all duration-200 group-hover:bg-opacity-30" />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-walrus-cream">
            <svg
              className="h-12 w-12 text-walrus-silver"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Info section */}
      <div className="p-3">
        {/* Filename */}
        <h3 className="truncate font-medium text-walrus-black text-sm" title={photo.filename}>
          {photo.filename}
        </h3>

        {/* Metadata */}
        <div className="mt-1 space-y-1 text-xs text-walrus-silver">
          {/* Uploader */}
          {photo.uploadedByName && (
            <p className="truncate">By {photo.uploadedByName}</p>
          )}

          {/* Date */}
          <p>{formatDate(photo.createdAt)}</p>

          {/* File size */}
          <p>{formatFileSize(photo.size)}</p>
        </div>

        {/* Caption if present */}
        {photo.caption && (
          <p className="mt-2 line-clamp-2 text-xs text-walrus-silver italic">
            {photo.caption}
          </p>
        )}
      </div>

      {/* Delete button - visible on hover for owners/admins */}
      {canDelete && onDelete && (
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="absolute top-2 right-2 rounded-lg bg-red-600 p-2 opacity-0 transition-all duration-200 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700"
          title="Delete photo"
          aria-label="Delete photo"
          type="button"
        >
          {isDeleting ? (
            <svg
              className="h-4 w-4 animate-spin text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          ) : (
            <svg
              className="h-4 w-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          )}
        </button>
      )}
    </div>
  );
};

PhotoCard.displayName = 'PhotoCard';

export default PhotoCard;
